import React, { useEffect, useState } from 'react';
import {
  useNotification,
  getDataGridHeading,
  pageSizeChangeHandler,
} from 'utils/commonUtils';
import {
  tutoringSubjectsColumns,
  getSubjectTimeBlocksListData,
} from './getSubjectTimeBlocksListData';
import MDBox from 'components/MDBox';
import Grid from '@mui/material/Grid';
import { useForm } from 'react-hook-form';
import { intialPageSize } from 'constants';
import { GridWrapper } from 'components/Ui/styled';
import ManageTimeBlocksDialog from './ManageTimeBlocksDialog';
import DeltaDataGrid from 'utils/CommonDataGrid/CommonDataGrid';
import FormInputDropdown from 'components/Common/Forms/FormInputDropDown';
import {
  getTimeBlock,
  getTutoringSubjects,
  getSubjectTimeBlocks,
} from 'layouts/tutoring/services/tutoringApi';
import CommonDeleteDialog from 'components/Common/CommonDeleteDialog/CommonDeleteDialog';
import { deleteSubjectTimeBlock } from 'layouts/tutoring/services/tutoringApi';

const ManageTimeBlocksTab = () => {
  const { NotificationPopup, handleErrorResponse } = useNotification();

  const [subjects, setSubjects] = useState([]);
  const [timeBlockId, setTimeBlockId] = useState(null);
  const [newTimeBlock, setNewTimeBlock] = useState(false);
  const [timeBlockForm, setTimeBlockForm] = useState(null);
  const [isFormLoading, setIsFormLoading] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [manageTimeBlocksDialog, setManageTimeBlocksDialog] = useState(false);
  const [selectedSubjectId, setSelectedSubjectId] = useState(null);
  const [subjectTimeBlocks, setSubjectTimeBlocks] = useState({
    isLoading: false,
    rows: [],
    pageSize: intialPageSize,
    page: 1,
  });

  const defaultValue = {
    subject: null,
  };

  const { reset, control, watch } = useForm({
    mode: 'onChange',
    defaultValues: defaultValue,
  });

  const selectedSubject = watch('subject');

  const subjectOptions = subjects?.map((item) => ({
    id: item.subjectId,
    value: item.name,
  }));

  useEffect(() => {
    fetchTutoringSubjects();
  }, []);

  useEffect(() => {
    if (selectedSubject && selectedSubject !== selectedSubjectId) {
      setSelectedSubjectId(selectedSubject);
      fetchSubjectTimeBlocks(selectedSubject);
    }
  }, [selectedSubject]);

  const fetchTutoringSubjects = async (retainSelectedSubjectId = null) => {
    try {
      const subjectAndTutorsData = await getTutoringSubjects();
      setSubjects(subjectAndTutorsData);

      if (subjectAndTutorsData?.length > 0) {
        const selectedSubjectToSet =
          retainSelectedSubjectId || subjectAndTutorsData[0]?.subjectId;
        setSelectedSubjectId(selectedSubjectToSet);
        reset({ subject: selectedSubjectToSet });
        await fetchSubjectTimeBlocks(
          selectedSubjectToSet,
          subjectAndTutorsData
        );
      }
    } catch (error) {
      handleErrorResponse(error?.response?.data?.message);
    }
  };

  const fetchSubjectTimeBlocks = async (subjectId, subjectAndTutorsData) => {
    try {
      setSubjectTimeBlocks((prev) => ({
        ...prev,
        isLoading: true,
      }));
      const getSubjects = subjectAndTutorsData || subjects;
      const getSubjectTimeBlocksData = await getSubjectTimeBlocks(subjectId);
      setSubjectTimeBlocks((prev) => ({
        ...prev,
        isLoading: false,
        rows: getSubjectTimeBlocksListData(
          getSubjectTimeBlocksData?.timeBlocks,
          getSubjects
        ),
      }));
    } catch (error) {
      setSubjectTimeBlocks((prev) => ({
        ...prev,
        isLoading: false,
      }));
      handleErrorResponse(error?.response?.data?.message);
    }
  };

  const pageSizeChange = pageSizeChangeHandler(setSubjectTimeBlocks);

  const handleEdit = async (params) => {
    const { timeBlockId } = params?.row;
    try {
      setIsFormLoading(true);
      const timeBlockData = await getTimeBlock(timeBlockId);
      setTimeBlockForm(timeBlockData);
      setManageTimeBlocksDialog(true);
      setIsFormLoading(false);
    } catch (error) {
      handleErrorResponse(error?.response?.data?.message);
      setIsFormLoading(false);
    }
  };

  const handleDelete = async (params) => {
    const { timeBlockId } = params?.row;
    try {
      setTimeBlockId(timeBlockId);
      setOpenDeleteDialog(true);
    } catch (error) {
      handleErrorResponse(error?.response?.data?.message);
    }
  };

  const handleNewTimeBlock = () => {
    setNewTimeBlock(true);
    setManageTimeBlocksDialog(true);
  };

  const fetchApi = () => {
    fetchTutoringSubjects(selectedSubjectId);
    if (selectedSubjectId) {
      fetchSubjectTimeBlocks();
    }
  };

  return (
    <>
      <MDBox mt={1}></MDBox>
      <GridWrapper container spacing={2}>
        <Grid item xs={12} sm={3}>
          <FormInputDropdown
            name="subject"
            type="text"
            control={control}
            options={subjectOptions}
            label="Subject"
          />
        </Grid>
        <Grid item xs={12} sm={9}>
          {getDataGridHeading('', 'New Time Block', handleNewTimeBlock)}
        </Grid>
      </GridWrapper>
      <DeltaDataGrid
        listData={subjectTimeBlocks}
        columns={tutoringSubjectsColumns(handleEdit, handleDelete)}
        setListData={pageSizeChange}
      />
      <ManageTimeBlocksDialog
        subjects={subjects}
        newTimeBlock={newTimeBlock}
        isFormLoading={isFormLoading}
        open={manageTimeBlocksDialog}
        timeBlockForm={timeBlockForm}
        setNewTimeBlock={setNewTimeBlock}
        setOpen={setManageTimeBlocksDialog}
        setTimeBlockForm={setTimeBlockForm}
        fetchTutoringSubjects={fetchTutoringSubjects}
        fetchSubjectTimeBlocks={fetchSubjectTimeBlocks}
      />
      <CommonDeleteDialog
        fetchData={fetchApi}
        snakBarName="Time Block"
        commonDeleteId={timeBlockId}
        openDeleteDialog={openDeleteDialog}
        commonDeleteApi={deleteSubjectTimeBlock}
        setOpenDeleteDialog={setOpenDeleteDialog}
        contentText="Are you sure you want to delete this time block ?"
      />
      <NotificationPopup />
    </>
  );
};

export default ManageTimeBlocksTab;
